/**
 * @fileoverview Framework Selector Component with Mobile-Optimized Interface
 * This component provides a modal interface for selecting between different
 * Saudi Labor Law categories, optimized for mobile touch interactions.
 * 
 * Supported Categories:
 * - Auto Detect - Automatically identify the relevant chapter
 * - Individual Chapters - All 16 chapters of the Saudi Labor Law
 * 
 * @module components/chat/framework-selector
 */

import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose
} from "@/components/ui/dialog";
import { TouchScrollArea } from "@/components/ui/scroll-area";
import { FileText, Check, Search } from "lucide-react";
import { useHapticFeedback } from "@/hooks/use-haptic";
import { cn } from "@/lib/utils";
import { Framework } from "@/types/framework";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLanguage } from "@/lib/language-context";
import { LocaleType } from "@/types/chat";
import { useTranslation } from "@/lib/translation-utils";

/**
 * Props for the FrameworkSelector component
 * @interface FrameworkSelectorProps
 */
type FrameworkSelectorProps = {
  /** Currently selected framework */
  selectedFramework: Framework;
  /** Callback function when framework selection changes */
  onFrameworkChange: (framework: Framework) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Additional className for styling */
  className?: string;
};

/**
 * Framework selector component with mobile-optimized interface
 * @param {FrameworkSelectorProps} props - Component props
 * @returns {JSX.Element} Rendered component
 */
export function FrameworkSelector({ 
  selectedFramework, 
  onFrameworkChange, 
  disabled,
  className
}: FrameworkSelectorProps) {
  const { triggerHaptic } = useHapticFeedback();
  const isMobile = useIsMobile();
  const { locale } = useLanguage();
  const { t } = useTranslation();
  const isRTL = locale === 'ar';
  const [open, setOpen] = React.useState(false);

  /**
   * Handles framework selection change with haptic feedback
   * @param {Framework} framework - Selected framework
   */
  const handleFrameworkChange = async (framework: Framework) => {
    try {
      await triggerHaptic('selection');
      onFrameworkChange(framework);
      setOpen(false); // Close the dialog after selection
    } catch (error) {
      console.warn('[FrameworkSelector] Haptic feedback failed:', error);
      onFrameworkChange(framework);
      setOpen(false); // Close the dialog after selection
    }
  };

  /**
   * Gets display name for framework - multilingual support
   * @param {Framework} framework - Framework type
   * @returns {string} Display name
   */
  const getFrameworkDisplayName = (framework: Framework) => {
    if (framework === "AUTO") {
      return t("Auto Detect");
    }

    // Localized chapter titles with article ranges
    const categoryNames: Record<LocaleType, Record<string, string>> = {
      // English - complete set
      en: {
        "CHAPTER_1": "Chapter 1: Definitions & General Provisions (Articles 1-21)",
        "CHAPTER_2": "Chapter 2: Application of Labor Law (Articles 22-31)",
        "CHAPTER_3": "Chapter 3: Employment of Workers (Articles 32-41)",
        "CHAPTER_4": "Chapter 4: Training & Qualification (Articles 42-49)",
        "CHAPTER_5": "Chapter 5: Work Relations (Articles 50-88)",
        "CHAPTER_6": "Chapter 6: Work Conditions & Circumstances (Articles 89-118)",
        "CHAPTER_7": "Chapter 7: Part-time Work (Articles 119-120)",
        "CHAPTER_8": "Chapter 8: Work Protection & Major Industrial Accidents (Articles 121-148)",
        "CHAPTER_9": "Chapter 9: Women's Employment (Articles 149-160)",
        "CHAPTER_10": "Chapter 10: Juvenile Employment (Articles 161-167)",
        "CHAPTER_11": "Chapter 11: Maritime Labor Contract (Articles 168-184)",
        "CHAPTER_12": "Chapter 12: Working in Mines & Quarries (Articles 185-193)",
        "CHAPTER_13": "Chapter 13: Labor Inspection (Articles 194-209)",
        "CHAPTER_14": "Chapter 14: Commissions for Settlement of Labor Disputes (Articles 210-228)",
        "CHAPTER_15": "Chapter 15: Penalties (Articles 229-242)",
        "CHAPTER_16": "Chapter 16: Concluding Provisions (Articles 243-245)"
      },
      // Arabic - exact titles as provided
      ar: {
        "CHAPTER_1": "الباب الأول: التعريفات والأحكام العامة (المواد 1-21)",
        "CHAPTER_2": "الباب الثاني: تنظيم عمليات التوظيف (المواد 22-31)",
        "CHAPTER_3": "الباب الثالث: توظيف غير السعوديين (المواد 32-41)",
        "CHAPTER_4": "الباب الرابع: التدريب والتأهيل (المواد 42-49)",
        "CHAPTER_5": "الباب الخامس: علاقات العمل (المواد 50-88)",
        "CHAPTER_6": "الباب السادس: شروط العمل وظروفه (المواد 89-118)",
        "CHAPTER_7": "الباب السابع: العمل لبعض الوقت (المواد 119-120)",
        "CHAPTER_8": "الباب الثامن: الوقاية من مخاطر العمل والوقاية من الحوادث الصناعية الكبرى وإصابات العمل والخدمات الصحية والاجتماعية (المواد 121-148)",
        "CHAPTER_9": "الباب التاسع: تشغيل النساء (المواد 149-160)",
        "CHAPTER_10": "الباب العاشر: تشغيل الأحداث (المواد 161-167)",
        "CHAPTER_11": "الباب الحادي عشر: عقد العمل البحري (المواد 168-184)",
        "CHAPTER_12": "الباب الثاني عشر: العمل في المناجم والمحاجر (المواد 185-193)",
        "CHAPTER_13": "الباب الثالث عشر: تفتيش العمال (المواد 194-209)", 
        "CHAPTER_14": "الباب الرابع عشر: هيئات تسوية الخلافات العمالية (المواد 210-228)",
        "CHAPTER_15": "الباب الخامس عشر: العقوبات (المواد 229-242)",
        "CHAPTER_16": "الباب السادس عشر: أحكام ختامية (المواد 243-245)"
      },
      // Chinese
      zh: {
        "CHAPTER_1": "第1章：定义和一般规定（第1-21条）",
        "CHAPTER_2": "第2章：劳动法的适用（第22-31条）",
        "CHAPTER_3": "第3章：工人就业（第32-41条）",
        "CHAPTER_4": "第4章：培训和资格（第42-49条）",
        "CHAPTER_5": "第5章：工作关系（第50-88条）",
        "CHAPTER_6": "第6章：工作条件和环境（第89-118条）",
        "CHAPTER_7": "第7章：兼职工作（第119-120条）",
        "CHAPTER_8": "第8章：工作保护和重大工业事故（第121-148条）",
        "CHAPTER_9": "第9章：女性就业（第149-160条）",
        "CHAPTER_10": "第10章：青少年就业（第161-167条）",
        "CHAPTER_11": "第11章：海事劳动合同（第168-184条）",
        "CHAPTER_12": "第12章：矿山和采石场工作（第185-193条）",
        "CHAPTER_13": "第13章：劳动检查（第194-209条）",
        "CHAPTER_14": "第14章：劳动争议解决委员会（第210-228条）",
        "CHAPTER_15": "第15章：处罚（第229-242条）",
        "CHAPTER_16": "第16章：结束条款（第243-245条）"
      },
      // Japanese
      ja: {
        "CHAPTER_1": "第1章：定義および一般規定（第1-21条）",
        "CHAPTER_2": "第2章：労働法の適用（第22-31条）",
        "CHAPTER_3": "第3章：労働者の雇用（第32-41条）",
        "CHAPTER_4": "第4章：訓練と資格（第42-49条）",
        "CHAPTER_5": "第5章：労働関係（第50-88条）",
        "CHAPTER_6": "第6章：労働条件と環境（第89-118条）",
        "CHAPTER_7": "第7章：パートタイム労働（第119-120条）",
        "CHAPTER_8": "第8章：労働保護と重大産業事故（第121-148条）",
        "CHAPTER_9": "第9章：女性雇用（第149-160条）",
        "CHAPTER_10": "第10章：若年者雇用（第161-167条）",
        "CHAPTER_11": "第11章：海事労働契約（第168-184条）",
        "CHAPTER_12": "第12章：鉱山と採石場での労働（第185-193条）",
        "CHAPTER_13": "第13章：労働監査（第194-209条）",
        "CHAPTER_14": "第14章：労働紛争解決委員会（第210-228条）",
        "CHAPTER_15": "第15章：罰則（第229-242条）",
        "CHAPTER_16": "第16章：最終規定（第243-245条）"
      },
      // Russian
      ru: {
        "CHAPTER_1": "Глава 1: Определения и общие положения (Статьи 1-21)",
        "CHAPTER_2": "Глава 2: Применение трудового законодательства (Статьи 22-31)",
        "CHAPTER_3": "Глава 3: Трудоустройство работников (Статьи 32-41)",
        "CHAPTER_4": "Глава 4: Обучение и квалификация (Статьи 42-49)",
        "CHAPTER_5": "Глава 5: Трудовые отношения (Статьи 50-88)",
        "CHAPTER_6": "Глава 6: Условия и обстоятельства труда (Статьи 89-118)",
        "CHAPTER_7": "Глава 7: Неполный рабочий день (Статьи 119-120)",
        "CHAPTER_8": "Глава 8: Охрана труда и крупные промышленные аварии (Статьи 121-148)",
        "CHAPTER_9": "Глава 9: Женская занятость (Статьи 149-160)",
        "CHAPTER_10": "Глава 10: Труд несовершеннолетних (Статьи 161-167)",
        "CHAPTER_11": "Глава 11: Договор морского труда (Статьи 168-184)",
        "CHAPTER_12": "Глава 12: Работа в шахтах и карьерах (Статьи 185-193)",
        "CHAPTER_13": "Глава 13: Трудовая инспекция (Статьи 194-209)",
        "CHAPTER_14": "Глава 14: Комиссии по разрешению трудовых споров (Статьи 210-228)",
        "CHAPTER_15": "Глава 15: Наказания (Статьи 229-242)",
        "CHAPTER_16": "Глава 16: Заключительные положения (Статьи 243-245)"
      },
      // German
      de: {
        "CHAPTER_1": "Kapitel 1: Definitionen & Allgemeine Bestimmungen (Artikel 1-21)",
        "CHAPTER_2": "Kapitel 2: Anwendung des Arbeitsrechts (Artikel 22-31)",
        "CHAPTER_3": "Kapitel 3: Beschäftigung von Arbeitnehmern (Artikel 32-41)",
        "CHAPTER_4": "Kapitel 4: Ausbildung & Qualifikation (Artikel 42-49)",
        "CHAPTER_5": "Kapitel 5: Arbeitsbeziehungen (Artikel 50-88)",
        "CHAPTER_6": "Kapitel 6: Arbeitsbedingungen & Umstände (Artikel 89-118)",
        "CHAPTER_7": "Kapitel 7: Teilzeitarbeit (Artikel 119-120)",
        "CHAPTER_8": "Kapitel 8: Arbeitsschutz & Schwere Industrieunfälle (Artikel 121-148)",
        "CHAPTER_9": "Kapitel 9: Frauenbeschäftigung (Artikel 149-160)",
        "CHAPTER_10": "Kapitel 10: Jugendbeschäftigung (Artikel 161-167)",
        "CHAPTER_11": "Kapitel 11: Seeschifffahrtsarbeitsvertrag (Artikel 168-184)",
        "CHAPTER_12": "Kapitel 12: Arbeit in Bergwerken & Steinbrüchen (Artikel 185-193)",
        "CHAPTER_13": "Kapitel 13: Arbeitsaufsicht (Artikel 194-209)",
        "CHAPTER_14": "Kapitel 14: Kommissionen zur Beilegung von Arbeitsstreitigkeiten (Artikel 210-228)",
        "CHAPTER_15": "Kapitel 15: Strafen (Artikel 229-242)",
        "CHAPTER_16": "Kapitel 16: Schlussbestimmungen (Artikel 243-245)"
      },
      // French
      fr: {
        "CHAPTER_1": "Chapitre 1 : Définitions et dispositions générales (Articles 1-21)",
        "CHAPTER_2": "Chapitre 2 : Application du droit du travail (Articles 22-31)",
        "CHAPTER_3": "Chapitre 3 : Emploi des travailleurs (Articles 32-41)",
        "CHAPTER_4": "Chapitre 4 : Formation et qualification (Articles 42-49)",
        "CHAPTER_5": "Chapitre 5 : Relations de travail (Articles 50-88)",
        "CHAPTER_6": "Chapitre 6 : Conditions et circonstances de travail (Articles 89-118)",
        "CHAPTER_7": "Chapitre 7 : Travail à temps partiel (Articles 119-120)",
        "CHAPTER_8": "Chapitre 8 : Protection du travail et accidents industriels majeurs (Articles 121-148)",
        "CHAPTER_9": "Chapitre 9 : Emploi des femmes (Articles 149-160)",
        "CHAPTER_10": "Chapitre 10 : Emploi des jeunes (Articles 161-167)",
        "CHAPTER_11": "Chapitre 11 : Contrat de travail maritime (Articles 168-184)",
        "CHAPTER_12": "Chapitre 12 : Travail dans les mines et carrières (Articles 185-193)",
        "CHAPTER_13": "Chapitre 13 : Inspection du travail (Articles 194-209)",
        "CHAPTER_14": "Chapitre 14 : Commissions de règlement des conflits du travail (Articles 210-228)",
        "CHAPTER_15": "Chapitre 15 : Sanctions (Articles 229-242)",
        "CHAPTER_16": "Chapitre 16 : Dispositions finales (Articles 243-245)"
      },
      // Spanish
      es: {
        "CHAPTER_1": "Capítulo 1: Definiciones y disposiciones generales (Artículos 1-21)",
        "CHAPTER_2": "Capítulo 2: Aplicación de la ley laboral (Artículos 22-31)",
        "CHAPTER_3": "Capítulo 3: Empleo de trabajadores (Artículos 32-41)",
        "CHAPTER_4": "Capítulo 4: Formación y cualificación (Artículos 42-49)",
        "CHAPTER_5": "Capítulo 5: Relaciones laborales (Artículos 50-88)",
        "CHAPTER_6": "Capítulo 6: Condiciones y circunstancias laborales (Artículos 89-118)",
        "CHAPTER_7": "Capítulo 7: Trabajo a tiempo parcial (Artículos 119-120)",
        "CHAPTER_8": "Capítulo 8: Protección laboral y accidentes industriales graves (Artículos 121-148)",
        "CHAPTER_9": "Capítulo 9: Empleo femenino (Artículos 149-160)",
        "CHAPTER_10": "Capítulo 10: Empleo juvenil (Artículos 161-167)",
        "CHAPTER_11": "Capítulo 11: Contrato laboral marítimo (Artículos 168-184)",
        "CHAPTER_12": "Capítulo 12: Trabajo en minas y canteras (Artículos 185-193)",
        "CHAPTER_13": "Capítulo 13: Inspección laboral (Artículos 194-209)",
        "CHAPTER_14": "Capítulo 14: Comisiones para la resolución de conflictos laborales (Artículos 210-228)",
        "CHAPTER_15": "Capítulo 15: Sanciones (Artículos 229-242)",
        "CHAPTER_16": "Capítulo 16: Disposiciones finales (Artículos 243-245)"
      },
      // Hindi
      hi: {
        "CHAPTER_1": "अध्याय 1: परिभाषाएँ और सामान्य प्रावधान (धारा 1-21)",
        "CHAPTER_2": "अध्याय 2: श्रम कानून का अनुप्रयोग (धारा 22-31)",
        "CHAPTER_3": "अध्याय 3: कामगारों का रोजगार (धारा 32-41)",
        "CHAPTER_4": "अध्याय 4: प्रशिक्षण और योग्यता (धारा 42-49)",
        "CHAPTER_5": "अध्याय 5: कार्य संबंध (धारा 50-88)",
        "CHAPTER_6": "अध्याय 6: कार्य परिस्थितियां और परिस्थितियां (धारा 89-118)",
        "CHAPTER_7": "अध्याय 7: अंशकालिक कार्य (धारा 119-120)",
        "CHAPTER_8": "अध्याय 8: कार्य सुरक्षा और प्रमुख औद्योगिक दुर्घटनाएं (धारा 121-148)",
        "CHAPTER_9": "अध्याय 9: महिला रोजगार (धारा 149-160)",
        "CHAPTER_10": "अध्याय 10: किशोर रोजगार (धारा 161-167)",
        "CHAPTER_11": "अध्याय 11: समुद्री श्रम अनुबंध (धारा 168-184)",
        "CHAPTER_12": "अध्याय 12: खानों और खदानों में काम (धारा 185-193)",
        "CHAPTER_13": "अध्याय 13: श्रम निरीक्षण (धारा 194-209)",
        "CHAPTER_14": "अध्याय 14: श्रम विवादों के निपटारे के लिए आयोग (धारा 210-228)",
        "CHAPTER_15": "अध्याय 15: दंड (धारा 229-242)",
        "CHAPTER_16": "अध्याय 16: समापन प्रावधान (धारा 243-245)"
      },
      // Turkish
      tr: {
        "CHAPTER_1": "Bölüm 1: Tanımlar ve Genel Hükümler (Madde 1-21)",
        "CHAPTER_2": "Bölüm 2: İş Kanunu Uygulaması (Madde 22-31)",
        "CHAPTER_3": "Bölüm 3: İşçilerin İstihdamı (Madde 32-41)",
        "CHAPTER_4": "Bölüm 4: Eğitim ve Yeterlilik (Madde 42-49)",
        "CHAPTER_5": "Bölüm 5: İş İlişkileri (Madde 50-88)",
        "CHAPTER_6": "Bölüm 6: Çalışma Koşulları ve Şartları (Madde 89-118)",
        "CHAPTER_7": "Bölüm 7: Yarı Zamanlı Çalışma (Madde 119-120)",
        "CHAPTER_8": "Bölüm 8: İş Koruması ve Büyük Endüstriyel Kazalar (Madde 121-148)",
        "CHAPTER_9": "Bölüm 9: Kadın İstihdamı (Madde 149-160)",
        "CHAPTER_10": "Bölüm 10: Genç İstihdamı (Madde 161-167)",
        "CHAPTER_11": "Bölüm 11: Deniz İş Sözleşmesi (Madde 168-184)",
        "CHAPTER_12": "Bölüm 12: Madenlerde ve Taş Ocaklarında Çalışma (Madde 185-193)",
        "CHAPTER_13": "Bölüm 13: İş Teftişi (Madde 194-209)",
        "CHAPTER_14": "Bölüm 14: İş Uyuşmazlıklarının Çözümü Komisyonları (Madde 210-228)",
        "CHAPTER_15": "Bölüm 15: Cezalar (Madde 229-242)",
        "CHAPTER_16": "Bölüm 16: Son Hükümler (Madde 243-245)"
      },
      // Swedish
      sv: {
        "CHAPTER_1": "Kapitel 1: Definitioner och allmänna bestämmelser (Artiklar 1-21)",
        "CHAPTER_2": "Kapitel 2: Tillämpning av arbetsrätt (Artiklar 22-31)",
        "CHAPTER_3": "Kapitel 3: Anställning av arbetstagare (Artiklar 32-41)",
        "CHAPTER_4": "Kapitel 4: Utbildning och kvalifikation (Artiklar 42-49)",
        "CHAPTER_5": "Kapitel 5: Arbetsrelationer (Artiklar 50-88)",
        "CHAPTER_6": "Kapitel 6: Arbetsvillkor och omständigheter (Artiklar 89-118)",
        "CHAPTER_7": "Kapitel 7: Deltidsarbete (Artiklar 119-120)",
        "CHAPTER_8": "Kapitel 8: Arbetsskydd och större industriolyckor (Artiklar 121-148)",
        "CHAPTER_9": "Kapitel 9: Kvinnors anställning (Artiklar 149-160)",
        "CHAPTER_10": "Kapitel 10: Ungdomsanställning (Artiklar 161-167)",
        "CHAPTER_11": "Kapitel 11: Sjöarbetsavtal (Artiklar 168-184)",
        "CHAPTER_12": "Kapitel 12: Arbete i gruvor och stenbrott (Artiklar 185-193)",
        "CHAPTER_13": "Kapitel 13: Arbetsinspektion (Artiklar 194-209)",
        "CHAPTER_14": "Kapitel 14: Kommissioner för lösning av arbetstvister (Artiklar 210-228)",
        "CHAPTER_15": "Kapitel 15: Straff (Artiklar 229-242)",
        "CHAPTER_16": "Kapitel 16: Slutbestämmelser (Artiklar 243-245)"
      }
    };
    
    // Default to English if the locale isn't available
    const currentLocaleNames = categoryNames[locale] || categoryNames.en;
    return currentLocaleNames[framework] || framework;
  };

  // Localized chapter group names
  const getLocalizedGroupNames = () => {
    const groupNames: Record<LocaleType, Record<string, string>> = {
      en: {
        "Employment Basics": "Employment Basics",
        "Working Conditions": "Working Conditions",
        "Special Categories": "Special Categories",
        "Compliance & Resolution": "Compliance & Resolution"
      },
      ar: {
        "Employment Basics": "أساسيات التوظيف",
        "Working Conditions": "ظروف العمل",
        "Special Categories": "فئات خاصة",
        "Compliance & Resolution": "الامتثال والتسوية"
      },
      zh: {
        "Employment Basics": "就业基础",
        "Working Conditions": "工作条件",
        "Special Categories": "特殊类别",
        "Compliance & Resolution": "合规与解决方案"
      },
      ja: {
        "Employment Basics": "雇用の基本",
        "Working Conditions": "労働条件",
        "Special Categories": "特別カテゴリー",
        "Compliance & Resolution": "コンプライアンスと解決"
      },
      ru: {
        "Employment Basics": "Основы трудоустройства",
        "Working Conditions": "Условия труда",
        "Special Categories": "Особые категории",
        "Compliance & Resolution": "Соответствие и разрешение споров"
      },
      de: {
        "Employment Basics": "Grundlagen der Beschäftigung",
        "Working Conditions": "Arbeitsbedingungen",
        "Special Categories": "Besondere Kategorien",
        "Compliance & Resolution": "Einhaltung & Konfliktlösung"
      },
      fr: {
        "Employment Basics": "Bases de l'emploi",
        "Working Conditions": "Conditions de travail",
        "Special Categories": "Catégories spéciales",
        "Compliance & Resolution": "Conformité et résolution"
      },
      es: {
        "Employment Basics": "Fundamentos del empleo",
        "Working Conditions": "Condiciones laborales",
        "Special Categories": "Categorías especiales",
        "Compliance & Resolution": "Cumplimiento y resolución"
      },
      hi: {
        "Employment Basics": "रोज़गार की मूल बातें",
        "Working Conditions": "काम करने की स्थिति",
        "Special Categories": "विशेष श्रेणियां",
        "Compliance & Resolution": "अनुपालन और समाधान"
      },
      tr: {
        "Employment Basics": "İstihdam Temelleri",
        "Working Conditions": "Çalışma Koşulları",
        "Special Categories": "Özel Kategoriler",
        "Compliance & Resolution": "Uyum ve Çözüm"
      },
      sv: {
        "Employment Basics": "Anställningsgrunder",
        "Working Conditions": "Arbetsförhållanden",
        "Special Categories": "Särskilda kategorier",
        "Compliance & Resolution": "Efterlevnad och tvistlösning"
      }
    };

    // Use English as fallback
    return groupNames[locale] || groupNames.en;
  };

  const localizedGroupNames = getLocalizedGroupNames();

  // Define chapter groups for better organization
  const chapterGroups = [
    {
      name: localizedGroupNames["Employment Basics"] || "Employment Basics",
      chapters: [
        "CHAPTER_1", // General Provisions
        "CHAPTER_2", // Application of Labor Law
        "CHAPTER_3", // Employment of Workers
        "CHAPTER_4"  // Training & Qualification
      ]
    },
    {
      name: localizedGroupNames["Working Conditions"] || "Working Conditions",
      chapters: [
        "CHAPTER_5", // Work Relations
        "CHAPTER_6", // Work Conditions & Circumstances
        "CHAPTER_7", // Part-time Work 
        "CHAPTER_8"  // Work Protection
      ]
    },
    {
      name: localizedGroupNames["Special Categories"] || "Special Categories",
      chapters: [
        "CHAPTER_9",  // Women's Employment
        "CHAPTER_10", // Juvenile Employment
        "CHAPTER_11", // Maritime Labor
        "CHAPTER_12"  // Mines & Quarries
      ]
    },
    {
      name: localizedGroupNames["Compliance & Resolution"] || "Compliance & Resolution",
      chapters: [
        "CHAPTER_13", // Labor Inspection
        "CHAPTER_14", // Settlement of Labor Disputes
        "CHAPTER_15", // Penalties
        "CHAPTER_16"  // Concluding Provisions
      ]
    }
  ];

  // Get a simplified name for the button display - multilingual support
  const getShortFrameworkName = (framework: Framework) => {
    if (framework === "AUTO") {
      // Localized "Auto" text based on language
      const autoText: Record<LocaleType, string> = {
        en: "Auto",
        ar: "تلقائي",
        zh: "自动",
        ja: "自動",
        ru: "Авто",
        de: "Auto",
        fr: "Auto",
        es: "Auto",
        hi: "ऑटो",
        tr: "Oto",
        sv: "Auto"
      };
      return autoText[locale] || "Auto";
    }
    
    const chapterNum = framework.replace("CHAPTER_", "");
    // Localized chapter prefix based on language
    const chapterPrefix: Record<LocaleType, string> = {
      en: "Ch.",
      ar: "باب",
      zh: "章",
      ja: "章",
      ru: "Гл.",
      de: "Kap.",
      fr: "Ch.",
      es: "Cap.",
      hi: "अ.",
      tr: "Böl.",
      sv: "Kap."
    };
    return `${chapterPrefix[locale] || "Ch."} ${chapterNum}`;
  };

  // Chapter Selection Button Component - multilingual support
  const ChapterButton = React.memo(({ chapter, groupName }: { chapter: string, groupName: string }) => {
    const chapterNum = chapter.replace("CHAPTER_", "");
    const isSelected = selectedFramework === chapter;
    const fullName = getFrameworkDisplayName(chapter as Framework);
    
    // Extract content based on the language pattern
    // This handles different formats based on the language
    let title = "";
    let articles = "";
    
    // Create a safe match extractor helper function to handle potentially undefined matches
    const safeExtractMatch = (text: string, pattern: RegExp): string => {
      const match = text.match(pattern);
      return match && match[1] ? match[1] : "";
    };
    
    // Extraction pattern based on language
    // Special handling for Chinese and Japanese
    if (locale === 'zh') {
      // Chinese format
      const parts = fullName.split('：');
      if (parts.length > 1) {
        title = parts[1].split('（')[0].trim();
        articles = safeExtractMatch(fullName, /（(第.*?条)）/);
      } else {
        title = fullName;
      }
    } else if (locale === 'ja') {
      // Japanese format
      const parts = fullName.split('：');
      if (parts.length > 1) {
        title = parts[1].split('（')[0].trim();
        articles = safeExtractMatch(fullName, /（(第.*?条)）/);
      } else {
        title = fullName;
      }
    } else if (locale === 'ar') {
      // Arabic format uses parentheses for articles - المواد
      title = fullName.split(":")[1]?.split("(")[0]?.trim() || "";
      articles = safeExtractMatch(fullName, /\(المواد (.*?)\)/);
    } else {
      // Default format for other languages
      title = fullName.split(":")[1]?.split("(")[0]?.trim() || "";
      
      // Different matching patterns for different languages
      if (locale === 'ru') {
        articles = safeExtractMatch(fullName, /\(Статьи (.*?)\)/);
      } else if (locale === 'de') {
        articles = safeExtractMatch(fullName, /\(Artikel (.*?)\)/);
      } else if (locale === 'fr') {
        articles = safeExtractMatch(fullName, /\(Articles (.*?)\)/);
      } else if (locale === 'es') {
        articles = safeExtractMatch(fullName, /\(Artículos (.*?)\)/);
      } else if (locale === 'hi') {
        articles = safeExtractMatch(fullName, /\(धारा (.*?)\)/);
      } else if (locale === 'tr') {
        articles = safeExtractMatch(fullName, /\(Madde (.*?)\)/);
      } else if (locale === 'sv') {
        articles = safeExtractMatch(fullName, /\(Artiklar (.*?)\)/);
      } else {
        // Default English format
        articles = safeExtractMatch(fullName, /\(Articles (.*?)\)/);
      }
    }
    
    // Get localized chapter prefix
    const chapterPrefix = chapterNum ? getShortFrameworkName(`CHAPTER_${chapterNum}` as Framework).split(' ')[0] : "";
    
    return (
      <button
        type="button"
        onClick={() => handleFrameworkChange(chapter as Framework)}
        className={cn(
          "flex flex-col items-start justify-center w-full rounded-lg transition-colors",
          isMobile 
            ? "gap-1 px-4 py-4" 
            : "gap-0.5 px-4 py-3.5",
          isSelected 
            ? "bg-primary/20 border-2 border-primary/40 shadow-sm" 
            : "border border-gray-300 dark:border-gray-700 hover:bg-primary/10 hover:border-primary/30 hover:shadow-sm transition-all duration-200"
        )}
        aria-checked={isSelected}
      >
        <div className="flex items-center gap-1 w-full justify-between">
          <span className="font-medium text-base">{chapterPrefix} {chapterNum}</span>
          {isSelected && <Check className="h-4 w-4" aria-hidden="true" />}
        </div>
        <div className={cn(
          "text-gray-800 dark:text-gray-200 text-left font-medium",
          isMobile ? "text-sm" : "text-xs"
        )} title={title}>{title}</div>
        <div className="text-xs text-gray-600 dark:text-gray-400 text-left">{articles}</div>
        <div className="text-xs text-gray-500 dark:text-gray-500 text-left">{groupName}</div>
      </button>
    );
  });
  
  ChapterButton.displayName = "ChapterButton";

  // Content for the dialog
  const ChapterSelectorContent = React.memo(() => (
    <div className={cn(
        "py-3",
        isMobile ? "px-4" : "px-6"  // More padding on desktop
      )}>
      <div className={cn(
        isMobile ? "space-y-5" : "space-y-5"  // Consistent spacing on desktop
      )}>
        {/* Auto Detect Option */}
        <button
          type="button" 
          onClick={() => handleFrameworkChange("AUTO")}
          className={cn(
            "flex items-center w-full gap-3 rounded-lg transition-colors",
            isMobile ? "px-4 py-4" : "px-4 py-3.5",
            selectedFramework === "AUTO" 
              ? "bg-primary/20 border-2 border-primary/40 shadow-sm" 
              : "border border-gray-300 dark:border-gray-700 hover:bg-primary/10 hover:border-primary/30 hover:shadow-sm transition-all duration-200"
          )}
          aria-checked={selectedFramework === "AUTO"}
        >
          <div className={cn(
            "flex-shrink-0 flex items-center justify-center bg-primary/10 rounded-full",
            isMobile ? "w-12 h-12" : "w-9 h-9"
          )}>
            <Search className={cn(
              "text-primary",
              isMobile ? "h-6 w-6" : "h-5 w-5"
            )} />
          </div>
          <div className="flex-1 text-left">
            <div className={cn(
              "font-semibold text-gray-800 dark:text-gray-200",
              isMobile ? "text-lg" : "text-base"
            )}>
              {(() => {
                // Localized "Auto Detect" text based on language - same as in the button
                const autoDetectText: Record<LocaleType, string> = {
                  en: "Auto Detect",
                  ar: "تحديد تلقائي",
                  zh: "自动检测",
                  ja: "自動検出",
                  ru: "Автоопределение",
                  de: "Automatische Erkennung",
                  fr: "Détection automatique",
                  es: "Detección automática",
                  hi: "स्वचालित पहचान",
                  tr: "Otomatik Algılama",
                  sv: "Automatisk identifiering"
                };
                return autoDetectText[locale] || autoDetectText.en;
              })()}
            </div>
            <div className={cn(
              "text-gray-600 dark:text-gray-400",
              isMobile ? "text-sm" : "text-xs"
            )}>
              {(() => {
                // Localized descriptions for Auto Detect feature
                const autoDetectDescription: Record<LocaleType, string> = {
                  en: "AI will identify the relevant chapter",
                  ar: "سيحدد الذكاء الاصطناعي الباب ذو الصلة",
                  zh: "AI 将识别相关章节",
                  ja: "AIが関連する章を特定します",
                  ru: "ИИ определит соответствующую главу",
                  de: "KI identifiziert das relevante Kapitel",
                  fr: "L'IA identifiera le chapitre pertinent",
                  es: "La IA identificará el capítulo relevante",
                  hi: "AI सम्बंधित अध्याय की पहचान करेगा",
                  tr: "AI ilgili bölümü tanımlayacak",
                  sv: "AI kommer att identifiera relevant kapitel"
                };
                return autoDetectDescription[locale] || autoDetectDescription.en;
              })()}
            </div>
          </div>
          {selectedFramework === "AUTO" && <Check className="h-5 w-5 flex-shrink-0 text-primary" aria-hidden="true" />}
        </button>
        
        {/* Chapter Groups */}
        {chapterGroups.map((group) => (
          <div key={group.name} className={cn(
            "space-y-2",
            isMobile ? "pt-2" : "pt-1"
          )}>
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-300 px-2">{group.name}</h3>
            <div className={cn(
              "grid",
              isMobile ? "grid-cols-1 gap-3" : "grid-cols-1 gap-2.5"
            )}>
              {group.chapters.map((chapter) => (
                <ChapterButton 
                  key={chapter} 
                  chapter={chapter} 
                  groupName={group.name}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  ));

  ChapterSelectorContent.displayName = "ChapterSelectorContent";

  // Render either a mobile-friendly dialog or desktop dropdown
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          className={cn(
            "model-selector-btn w-full h-10 justify-between",
            className
          )}
          disabled={disabled}
          aria-label={`Select chapter - currently using ${getShortFrameworkName(selectedFramework)}`}
        >
          <div className={cn(
            "flex items-center gap-2",
            isRTL ? "flex-row" : "" // In Arabic (RTL), text first then icon
          )}>
            {/* In RTL layouts (Arabic), we want the text first then icon */}
            {isRTL ? null : <FileText className="h-4 w-4" aria-hidden="true" />}
            <span className={cn(
              "font-semibold truncate",
              isRTL ? "text-right" : "text-left"
            )}>
              {(() => {
                if (selectedFramework === "AUTO") {
                  // Localized "Auto Detect" text based on language
                  const autoDetectText: Record<LocaleType, string> = {
                    en: "Auto Detect",
                    ar: "تحديد تلقائي",
                    zh: "自动检测",
                    ja: "自動検出",
                    ru: "Автоопределение",
                    de: "Automatische Erkennung",
                    fr: "Détection automatique",
                    es: "Detección automática",
                    hi: "स्वचालित पहचान",
                    tr: "Otomatik Algılama",
                    sv: "Automatisk identifiering"
                  };
                  return autoDetectText[locale] || autoDetectText.en;
                } else {
                  return getShortFrameworkName(selectedFramework);
                }
              })()}
            </span>
            {/* For RTL languages like Arabic, place icon after text */}
            {isRTL && <FileText className="h-4 w-4" aria-hidden="true" />}
          </div>
          {selectedFramework !== "AUTO" && (
            <span className={cn(
              "text-xs text-gray-500 truncate max-w-[70%]",
              isRTL ? "text-right" : "text-left"
            )}>
              {(() => {
                const fullName = getFrameworkDisplayName(selectedFramework);
                
                // Special handling for Chinese and Japanese
                if (locale === 'zh' || locale === 'ja') {
                  const parts = fullName.split('：');
                  return parts.length > 1 ? parts[1].split('（')[0].trim() : "";
                } else {
                  // Default for other languages
                  return fullName.split(isRTL ? ":" : ":")[1]?.split("(")[0]?.trim() || "";
                }
              })()}
            </span>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent 
        className={cn(
          "p-0 max-w-md w-[95%] rounded-xl flex flex-col",
          isMobile 
            ? "bottom-0 translate-y-0 top-auto max-h-[85vh]" 
            : "max-h-[70vh]"
        )}
      >
        <div className="px-4 pt-4 pb-2 flex-shrink-0">
          <DialogTitle className={cn(
            "text-center mx-auto block w-full",
            "relative" // Position relative for better control
          )}>
            {(() => {
              // Localized dialog title based on language
              const dialogTitle: Record<LocaleType, string> = {
                en: "Select Labor Law Chapter",
                ar: "اختر باب نظام العمل",
                zh: "选择劳动法章节",
                ja: "労働法の章を選択",
                ru: "Выберите главу трудового законодательства",
                de: "Kapitel des Arbeitsrechts auswählen",
                fr: "Sélectionner le chapitre du droit du travail",
                es: "Seleccionar capítulo de la Ley Laboral",
                hi: "श्रम कानून अध्याय चुनें",
                tr: "İş Kanunu Bölümünü Seçin",
                sv: "Välj kapitel i arbetsrätten"
              };
              return dialogTitle[locale] || dialogTitle.en;
            })()}
          </DialogTitle>
        </div>
        <div 
          className={cn(
            "overflow-y-auto",
            isMobile ? "pb-12" : "pb-4"  // Extra padding at bottom for mobile
          )}
          style={{
            WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
            msOverflowStyle: 'none',  // Hide scrollbar in Edge
            scrollbarWidth: 'none',   // Hide scrollbar in Firefox
          }}
        >
          <ChapterSelectorContent />
        </div>
      </DialogContent>
    </Dialog>
  );
}